// Basic
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
// Store and actions
import actionsPlans from '../../api/actionsPlans';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import ICONS from '@/common-utils/enums/icons';
import Permissions from '@/app/permissions/Permissions';
import plansFields from '../../fields/plansFields';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import TabularList from '@/common-utils/components/renders/tabularList/TabularList';
// Style
import '../../../../../common-utils/components/renders/detailsGrid/DetailsGrid.scss';
import { nsPlan } from '@/app/i18n/namespaces';

/**
 * @param root
 * @param root.data
 * @param root.isLoading
 * @param root.isFetching
 * @param root.setLaunchRefetch
 * @returns {JSX} AllowedClientsPanel
 */
function AllowedClientsPanel({ data, isLoading, isFetching, setLaunchRefetch }) {
    // Store
    const { uuid: loggedUserOrganizationId } = useRootOrganizationStore(s => s.rootOrganization);
    const { permissions: userPermissions } = useSessionStore(s => s.session);

    //Toast
    const { toast } = useToast();

    // URL Params
    const { planId } = useParams();

    // Api Calls
    const onSuccess = () => {
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsPlan} entity='toast.success.delete_access' />,
            variant: 'success'
        });
    };

    const onError = () => {
        toast({
            description: <Entity namespace={nsPlan} entity={'toast.error.delete_access'} />,
            variant: 'danger'
        });
    };

    const deleteAllowedClient = actionsPlans.deleteAllowedClient(onSuccess, onError);

    //Functions
    const handleDeleteAllowedClient = (allowedClient) => {
        deleteAllowedClient.mutate({
            organizationId: loggedUserOrganizationId,
            planId: planId,
            allowedClientId: allowedClient[plansFields.ALLOWED_CLIENTS.UUID.key]
        });
    };

    // Constants
    const dataRows = [
        {
            type: 'text',
            key: 'legalName'
        },
        {
            type: 'button',
            action: (allowedClient) => handleDeleteAllowedClient(allowedClient),
            title: 'actions.row.delete_access',
            icon: ICONS.CLOSE,
            entity: '',
            namespace: nsPlan,
            variant: 'danger',
            disabled: !hasRequiredPermissions({
                permissions: userPermissions,
                requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_DELETE_CLIENT.name]
            }) && !(deleteAllowedClient.isLoading || isFetching),
            tooltipForbidden: 'title.permissions.access_denied'
        }
    ];

    const filters = [
        { key: plansFields.ALLOWED_CLIENTS.LEGAL_NAME.key, entity: 'filter.placeholder.organization', namespace: nsPlan }
    ];

    return (
        <>
            {isLoading && <Spinner className='panel-spinner' />}
            {!isLoading && data &&
                <TabularList
                    data={data}
                    tabularRows={dataRows}
                    filters={filters}
                    cellClassName='sm:min-w-28 md:min-w-60 lg:min-w-96'
                />
            }
        </>
    );
}

AllowedClientsPanel.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default AllowedClientsPanel;